import React, { useEffect, useState, useCallback, useMemo } from "react";
import Pagination from "react-js-pagination";
import { useHistory } from "react-router-dom";
import ReactBSAlert from "react-bootstrap-sweetalert";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Container,
  Row,
  Col,
  Spinner,
  Button,
} from "reactstrap";

import subscriptionsService from "../../services/api/subscription-membershipl-service";

const RECORDS_PER_PAGE = 3;

const UserSubscriptions = ({ userId, projectId }) => {
  // State for subscriptions
  const history = useHistory();
  const [subscriptions, setSubscriptions] = useState([]);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);

  const [alert, setAlert] = useState(null);

  // Form state
  const formState = useMemo(
    () => ({
      user_id: userId,
      project_id: projectId,
    }),
    [userId, projectId]
  );

  const handleClickSubs = useCallback(
    (projectId, pg_subscription_id) => {
      if (!projectId || !pg_subscription_id) return;

      history.push(
        `/membership/transaction?pg_subscription_id=${pg_subscription_id}&projectid=${projectId}`
      );
    },
    [history]
  );

  // Create alert function
  const createAlert = useCallback((type, message) => {
    const alertProps = {
      style: { display: "block", marginTop: "-100px" },
      onConfirm: () => setAlert(null),
      onCancel: () => setAlert(null),
    };

    switch (type) {
      case "loading":
        return (
          <ReactBSAlert
            info
            {...alertProps}
            showConfirm={false}
            title="Processing..."
          >
            <div className="d-flex flex-column align-items-center">
              <Spinner className="mb-3" />
              <p>{message}</p>
            </div>
          </ReactBSAlert>
        );
      case "success":
        return (
          <ReactBSAlert
            success
            {...alertProps}
            title="Success"
            confirmBtnBsStyle="success"
          >
            {message}
          </ReactBSAlert>
        );
      case "error":
        return (
          <ReactBSAlert
            danger
            {...alertProps}
            title="Error"
            confirmBtnBsStyle="danger"
          >
            {message}
          </ReactBSAlert>
        );
      default:
        return null;
    }
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    if (!projectId || !userId) return;

    setIsSubscriptionLoading(true);
    try {
      const response = await subscriptionsService.getSubscriptions(
        formState,
        subscriptionPage,
        RECORDS_PER_PAGE
      );
      if (response.status && response.data) {
        console.log(response.data);
        setSubscriptions(response.data.subscriptions);
        setTotalSubscriptions(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching Memberships:", error);
      setAlert(createAlert("error", "Failed to fetch Memberships"));
    } finally {
      setIsSubscriptionLoading(false);
    }
  }, [projectId, userId, formState, subscriptionPage, createAlert]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  if (!projectId) {
    history.push("/project/allprojects");
  }

  return (
    <div>
      {alert}
      <Container>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <CardTitle tag="h4">Membership Dashboard</CardTitle>
                </div>
                {totalSubscriptions > 0 && (
                  <Row>
                    <Col md="6">
                      <div style={{ float: "right", margin: "5px" }}>
                        <b>
                          {(subscriptionPage - 1) * RECORDS_PER_PAGE + 1} -{" "}
                          {Math.min(
                            subscriptionPage * RECORDS_PER_PAGE,
                            totalSubscriptions
                          )}{" "}
                          of {totalSubscriptions}
                        </b>
                      </div>
                    </Col>
                    <Col md="6">
                      <div style={{ float: "left" }}>
                        <Pagination
                          activePage={subscriptionPage}
                          itemsCountPerPage={RECORDS_PER_PAGE}
                          totalItemsCount={totalSubscriptions}
                          pageRangeDisplayed={3}
                          onChange={setSubscriptionPage}
                          itemClass="page-item"
                          linkClass="page-link"
                        />
                      </div>
                    </Col>
                  </Row>
                )}
              </CardHeader>

              <CardBody>
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>S.no.</th>
                      <th>User Email or Phone No.</th>
                      <th>Membership ID</th>
                      <th>Paid Count</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isSubscriptionLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          <Spinner />
                        </td>
                      </tr>
                    ) : subscriptions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          <CardTitle tag="h4">No Records Found</CardTitle>
                        </td>
                      </tr>
                    ) : (
                      subscriptions.map((subscription, index) => (
                        <tr key={subscription._id}>
                          <td>
                            {(subscriptionPage - 1) * RECORDS_PER_PAGE +
                              index +
                              1}
                          </td>
                          <td>
                            {subscription.user_id?.email ||
                              subscription?.user_id?.phone_number ||
                              "-"}
                          </td>
                          <td
                            style={{ cursor: "pointer", color: "#0077b6" }}
                            onClick={() =>
                              handleClickSubs(
                                projectId,
                                subscription?.pg_subscription_id
                              )
                            }
                          >
                            {subscription.pg_subscription_id || ""}
                          </td>
                          <td>{subscription.paid_count}</td>
                          <td>
                            {subscription?.total_count === 1
                              ? "One Time"
                              : subscription?.total_count
                              ? "Recurring"
                              : "-"}
                          </td>
                          <td>{subscription.status}</td>
                          <td>
                            {subscription?.start_at
                              ? moment
                                  .unix(subscription?.start_at)
                                  .format("DD MMM YYYY, hh:mm A")
                              : "-"}
                          </td>
                          <td>
                            {subscription?.end_at
                              ? moment
                                  .unix(subscription?.end_at)
                                  .format("DD MMM YYYY, hh:mm A")
                              : "-"}
                          </td>

                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Button
                                color="info"
                                size="sm"
                                onClick={() =>
                                  history.push(
                                    `/membership/subscription-details/${subscription._id}`
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faInfo} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserSubscriptions;
